import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const useIsoLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

export default function AnchoredPortal({
  anchorRef,
  open,
  placement = "bottom-end",
  offset = 10,
  safeMargin = 10,
  // Should sit above header chrome but below modal dialogs.
  zIndex = 100550,
  children,
}) {
  const portalRoot = useMemo(() => {
    if (typeof document === "undefined") return null;
    return document.body;
  }, []);

  const [pos, setPos] = useState(null);
  const portalElRef = useRef(null);
  const [contentSize, setContentSize] = useState(null);

  useIsoLayoutEffect(() => {
    if (!open) return;
    const el = anchorRef?.current;
    if (!el) return;

    const compute = () => {
      const r = el.getBoundingClientRect();
      const w = contentSize?.w ?? 0;

      let top = 0;
      let x = 0;

      switch (placement) {
        case "bottom-start":
          top = r.bottom + offset;
          x = r.left;
          break;
        case "bottom-end":
        default:
          top = r.bottom + offset;
          x = r.right - w;
          break;
      }

      // Clamp horizontally so the dropdown stays within viewport.
      const vw = window.innerWidth || 0;
      const maxX = Math.max(safeMargin, vw - safeMargin - w);
      const clampedX = Math.min(Math.max(x, safeMargin), maxX);

      setPos({ top, left: clampedX });
    };

    compute();

    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, anchorRef, placement, offset, safeMargin, contentSize]);

  useIsoLayoutEffect(() => {
    if (!open) return;
    const node = portalElRef.current;
    if (!node) return;

    const measure = () => {
      const r = node.getBoundingClientRect();
      const w = Math.ceil(r.width);
      const h = Math.ceil(r.height);
      if (!w || !h) return;
      setContentSize((prev) =>
        prev?.w === w && prev?.h === h ? prev : { w, h },
      );
    };

    measure();

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => measure())
        : null;
    if (ro) ro.observe(node);

    return () => {
      if (ro) ro.disconnect();
    };
  }, [open]);

  if (!open || !portalRoot) return null;

  // Mount even before `pos` is known so the measure effect can size the node;
  // keep it hidden until positioned (layout effects settle before paint).
  const ready = pos != null;

  return createPortal(
    <div
      ref={portalElRef}
      style={{
        position: "fixed",
        top: ready ? pos.top : 0,
        left: ready ? pos.left : 0,
        zIndex,
        isolation: "isolate",
        maxWidth: `calc(100vw - ${safeMargin * 2}px)`,
        visibility: ready ? "visible" : "hidden",
        pointerEvents: ready ? undefined : "none",
      }}
    >
      {children}
    </div>,
    portalRoot,
  );
}
